import { api } from '../api/client';

import { API_COLLECTIONS_URL, API_PUBLIC_SHARE_URL, API_SHARE_LINKS_URL } from '../utils';
import { apiErrorCode } from '../utils/apiErrors';
import { SHARE_LINK_HOP_MESSAGE } from '../utils/collectionLinks';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * Temporary share links: a manager pins a TARGET, anyone holding the code
 * reads it with NO account until the link expires or is revoked.
 *
 * Four target kinds, all on the same code:
 *   - 'view'   — a list of items in the collection. `query` is OPTIONAL; with
 *                no query the link is the whole collection, unfiltered.
 *   - 'item'   — one pinned item (`item_id`).
 *   - 'card'   — one card definition, the template (`card_id`).
 *   - 'intake' — a FORM over one card (`card_id` + an explicit `fields`
 *                allowlist) that a visitor submits to create one quarantined
 *                item. The only kind that writes, and the only one that can
 *                be edited after it is made.
 * A link stored before kinds existed has none; the API reads those as 'view'.
 *
 * Two halves with opposite postures:
 *   - the management half (list/create/revoke) is manage-gated;
 *   - the public half (`/api/share/{code}[/items]`) takes no token and
 *     answers every failure — unknown, expired, revoked — with the SAME flat
 *     404. It never 401s, so the api client's login redirect can't fire on it
 *     and a logged-out visitor is never bounced off a shared view.
 *
 * The raw code exists exactly once, in the create response. A manager who
 * loses it revokes the link and makes a new one.
 */

const linkUrl = (collectionId, linkId) => `${API_SHARE_LINKS_URL(collectionId)}/${linkId}`;

/**
 * How long a new link lives. ONE list for every surface that mints one — the
 * Sharing tab's panel and the temp-link section inside every share modal —
 * so a link made from a modal and a link made from the panel can never offer
 * different lifetimes.
 */
export const EXPIRY_OPTIONS = [
	{ value: 1, label: '1 day' },
	{ value: 7, label: '7 days' },
	{ value: 30, label: '30 days' },
	{ value: 90, label: '90 days' },
];

export const DEFAULT_EXPIRY_DAYS = 7;

/**
 * What the one-time reveal has to say, wherever a link is minted. It lives
 * beside the expiry vocabulary for the same reason: the panel and the modal
 * section both reveal a code exactly once, and neither should be the other's
 * source of truth for how that is worded.
 */
export const ONE_TIME_CODE_NOTE =
	"Copy this link now — you won't see it again. If it's lost, revoke it and make a new one.";

/** Legacy rows carry no kind; the API reads those as 'view' and so do we. */
export const linkKind = link => link?.kind || 'view';

/**
 * "Include files" (PR 10c; owner rulings F10, G12, G13): a VIEW or ITEM link
 * may carry its items' files — file element values AND Other attachments —
 * to visitors. A card link has no items and an intake link only writes, so
 * neither ever offers it (the API refuses the key on them). A link made
 * before 10c has no `includeFiles` and is OFF, strictly; a new link made
 * from the web is ON unless the sharer turns it off.
 */
export const INCLUDE_FILES_KINDS = ['view', 'item'];
export const kindTakesFiles = kind => INCLUDE_FILES_KINDS.includes(kind || 'view');
export const linkIncludesFiles = link => kindTakesFiles(linkKind(link)) && link?.includeFiles === true;
/** A link whose files spent today's byte budget: visitors' file requests wait for tomorrow (429). */
export const linkAtDailyFileLimit = link => linkIncludesFiles(link) && link?.dailyFileLimitReached === true;

const countOf = value => (Number.isInteger(value) && value > 0 ? value : 0);

/**
 * The preview's `files` → `{ total, restricted }`, or null when the answer
 * carries none. `restricted` (files a visitor may not download, G15) never
 * exceeds `total`.
 */
export function readFilesCount(files) {
	if (!files || typeof files !== 'object') return null;
	const total = countOf(files.total);
	return { total, restricted: Math.min(total, countOf(files.restricted)) };
}

/** Active AND revoked/expired, newest first; each row carries `status`. Never a code. */
export async function getShareLinks(collectionId) {
	const { data } = await api.get(API_SHARE_LINKS_URL(collectionId));
	return Array.isArray(data?.links) ? data.links : [];
}

/**
 * 201 → {_id, code, url, expiresAt, name, kind}.
 *
 * Only the fields that belong to the chosen kind go up: the API rejects a
 * `query` on an item link, an `item_id` on a view link, and so on. `kind`
 * itself is omitted when it is the default, so the payload a plain view link
 * sends is exactly what it always sent.
 */
export async function createShareLink(collectionId, {
	kind = 'view',
	query,
	item_id: itemId,
	card_id: cardId,
	name,
	expiresInDays,
	includeFiles,
	// The person's answer to an ambiguous element name in a view's query
	// (utils/elementRefusal.js) — never with another kind.
	elementChoices = null,
	...intake
} = {}) {
	const payload = {};
	if (kind && kind !== 'view') payload.kind = kind;
	if (kind === 'item') payload.item_id = itemId;
	else if (kind === 'card' || kind === 'intake') payload.card_id = cardId;
	else if (query) payload.query = query;
	// Only the kinds that carry items take it; the API refuses it on the
	// others, so it is never sent with them.
	if (kindTakesFiles(kind) && typeof includeFiles === 'boolean') payload.includeFiles = includeFiles;
	// The intake payload is CLOSED on the API side (a stray key is a 400
	// unknown_key), and the reading kinds refuse the intake keys outright —
	// so they go up only with the kind that owns them.
	if (kind === 'intake') Object.assign(payload, intakeConfigPayload(intake));
	if (name) payload.name = name;
	if (expiresInDays) payload.expiresInDays = expiresInDays;
	const { data } = await api.post(API_SHARE_LINKS_URL(collectionId), withElementChoicesField(payload, payload.query ? elementChoices : null));
	return data;
}

/**
 * The intake half of a create or edit, in the API's spelling. `fields` is
 * REQUIRED and never "all of them": a link minted today must not start
 * collecting an element added to the card next quarter. Blank copy goes up
 * as null on an edit so clearing a headline actually clears it.
 */
function intakeConfigPayload({
	fields, prefill, headline, intro, confirmation, maxSubmissions, uploads, privateFields, shownFields, entryLimits, privateRequired,
} = {}, { edit = false } = {}) {
	const text = value => {
		const trimmed = typeof value === 'string' ? value.trim() : '';
		if (trimmed) return trimmed;
		return edit ? null : undefined;
	};
	const out = { fields: Array.isArray(fields) ? fields : [] };
	// KPR-389: the questions the form asks PRIVATELY — never in `fields`, their
	// answers never on the record. Always sent on an edit (an empty list
	// clears it): left out, the API keeps the stored list, and this form
	// knows the whole of it.
	if (Array.isArray(privateFields) && (privateFields.length || edit)) out.privateFields = privateFields;
	// KPR-519: the elements the form SHOWS read-only, at their default — sent
	// only when the editor says so (a list it has, or an empty one that clears
	// what the form had): a form that never showed anything goes up exactly as
	// it always did, and the API stores no key for it.
	if (Array.isArray(shownFields)) out.shownFields = shownFields;
	// KPR-689: the fewest and most entries per repeating group the form asks —
	// sent only when the editor says so (limits it sets, or an empty map that
	// clears what the form had), as `shownFields` is.
	if (entryLimits && typeof entryLimits === 'object' && !Array.isArray(entryLimits)) out.entryLimits = entryLimits;
	// KPR-803: the private questions the FORM requires the visitor to answer
	// (the card element stays optional) — sent only when the editor says so,
	// as `shownFields` is: a form that never required one goes up as before.
	if (Array.isArray(privateRequired)) out.privateRequired = privateRequired;
	if (prefill && Object.keys(prefill).length) out.prefill = prefill;
	else if (edit) out.prefill = {};
	['headline', 'intro', 'confirmation'].forEach((key, index) => {
		const value = text([headline, intro, confirmation][index]);
		if (value !== undefined) out[key] = value;
	});
	// No `allowNotes`: public forms no longer offer a notes box (KPR-792).
	if (Number.isInteger(maxSubmissions)) out.maxSubmissions = maxSubmissions;
	// KPR-388: what the form's file elements take — sent only with a file
	// field on the form (the API refuses it without one, and a PATCH that
	// leaves it out lets the stored one go with the last file field).
	if (uploads && typeof uploads === 'object') {
		out.uploads = {
			types: Array.isArray(uploads.types) ? uploads.types : ['photo'],
			maxFileMb: uploads.maxFileMb,
			maxSubmissionMb: uploads.maxSubmissionMb,
			maxFiles: uploads.maxFiles,
		};
	}
	return out;
}

/**
 * The definition half of a form, in the API's spelling — shared with the
 * permanent public form (services/publicForms.js), whose `definition` takes
 * exactly these keys and is judged by the same API module (KPR-513).
 */
export const definitionPayload = (definition, options) => intakeConfigPayload(definition, options);

/**
 * Change an INTAKE link: its copy, allowlist, prefill, cap, name or expiry
 * (`PATCH …/share-links/{linkId}` → the serialized link). The card never
 * changes — a form for another card is another link. The API re-validates
 * the merged configuration whole, so an edit cannot pass what a create would
 * refuse. `expiresInDays` is sent only when the manager chose a new expiry;
 * it counts from now.
 */
export async function updateIntakeLink(collectionId, linkId, {
	name, expiresInDays, ...config
} = {}) {
	const payload = intakeConfigPayload(config, { edit: true });
	payload.name = typeof name === 'string' && name.trim() ? name.trim() : null;
	if (expiresInDays) payload.expiresInDays = expiresInDays;
	const { data } = await api.patch(linkUrl(collectionId, linkId), payload);
	return data;
}

/**
 * The collection's intake switch (`PUT /api/collections/{id}/intake`, manage
 * + a verified session). It is an AUTHORIZATION field: the ordinary
 * collection save strips it, and only this door moves it. Off kills every
 * live intake link at once; on again revives those neither expired nor
 * revoked. Answers `{ _id, allowIntakeLinks }`.
 */
export async function setIntakeLinksAllowed(collectionId, allowIntakeLinks) {
	const { data } = await api.put(`${API_COLLECTIONS_URL}/${collectionId}/intake`, {
		allowIntakeLinks: allowIntakeLinks === true,
	});
	return data;
}

/** Soft revoke — the row stays in the listing as history. Idempotent. */
export async function revokeShareLink(collectionId, linkId) {
	const { data } = await api.delete(linkUrl(collectionId, linkId));
	return data;
}

/**
 * Turn "Include files" on or off for a view or item link (`PATCH
 * …/share-links/{linkId}` with `{ includeFiles }`, manage + a session; the
 * link's change-log row records it). Answers the serialized link.
 */
export async function setShareLinkIncludeFiles(collectionId, linkId, includeFiles) {
	const { data } = await api.patch(linkUrl(collectionId, linkId), { includeFiles: includeFiles === true });
	return data;
}

/**
 * What a view or item link would disclose in files, before it is made or
 * when its files are turned on: `GET …/share-links/preview` — `?kind=view&q=`
 * (the filter, none for the whole collection), `?kind=item&item_id=`, or an
 * existing link's `?link_id=` — → `{ kind, items, files: { total, restricted
 * }, attachmentsOn, capped }`, counted by the API over the items the link
 * reaches through the public read's scope (file elements and Other
 * attachments, hidden values' files left out). A read, so it writes nothing.
 * Answers `{ total, restricted }` (+ `capped: true` when the API counted only
 * its newest items), or null.
 */
export async function previewShareLinkFiles(collectionId, { kind = 'view', query, item_id: itemId, link_id: linkId } = {}) {
	const params = {};
	if (linkId) params.link_id = linkId;
	else {
		if (!kindTakesFiles(kind)) return null;
		params.kind = kind;
		if (kind === 'item') params.item_id = itemId;
		else if (query) params.q = query;
	}
	const { data } = await api.get(`${API_SHARE_LINKS_URL(collectionId)}/preview`, { params });
	const count = readFilesCount(data?.files);
	// Past the API's item cap it counted the newest items only (`capped`).
	return count && data?.capped === true ? { ...count, capped: true } : count;
}

// ------------------------------------------------------------------ public

/**
 * The link's target descriptor — no auth needed. Always carries `kind`:
 *   'view'   → { kind, collection, cards, name, expiresAt }
 *   'item'   → the same plus `item` (the card chain is in `cards`)
 *   'card'   → the card plus its ancestors in `cards`
 *   'intake' → a FORM and nothing else: { kind, collection, name, expiresAt,
 *              headline, intro, card: {_id, name, label, icon,
 *              color, description}, elements: [the allowlisted definitions,
 *              in the manager's order], submissionToken, botCheck:
 *              {provider, siteKey | null} | null }. No items, no ancestors,
 *              no element sets, nothing of the prefill. The link's private
 *              questions (KPR-389) follow its public ones in `elements`, each
 *              `private: true`; `collection.published` is true when the
 *              collection is a published global collection, whose accepted
 *              records every signed-in account reads.
 * Every failure, dead target included, is the same flat 404.
 *
 * `?uploads=1` (KPR-388): this page can send a public form's photos
 * (components/common/PublicFileField), so an intake form may carry its
 * `file` elements — each with `upload: { types, maxFileMb }`, and the form
 * `uploads: { maxFiles, maxSubmissionMb }`. The API leaves them off for a
 * page that does not say so (an older build would render the signed-in
 * uploader). Every other kind ignores it.
 *
 * `?shown=1` (KPR-519): this page renders a READ-ONLY element
 * (pages/SharedFormPage), so the link's shown elements — those an element
 * rule lets the public see but not change — come too, each `readOnly: true`
 * with the `value` the record will hold. The API leaves them off for a page
 * that does not say so (an older build would render them as questions).
 */
export async function getSharedView(code, { uploads = true, shown = true } = {}) {
	const url = `${API_PUBLIC_SHARE_URL}/${encodeURIComponent(code)}`;
	const params = {};
	if (uploads) params.uploads = 1;
	if (shown) params.shown = 1;
	const { data } = Object.keys(params).length ? await api.get(url, { params }) : await api.get(url);
	return data;
}

/**
 * One page of the shared items plus the X-Total-Count total — no auth needed.
 * 'view' links only; an item/card link answers the same flat 404 here.
 */
export async function getSharedItems(code, { limit, skip } = {}) {
	const { data, headers } = await api.get(
		`${API_PUBLIC_SHARE_URL}/${encodeURIComponent(code)}/items`,
		{ params: { limit, skip } }
	);
	// The endpoint is migrating from a bare array + X-Total-Count header to an
	// { items, totalCount, lookupTitles } envelope (lookupTitles maps each
	// referenced item id the link is allowed to disclose to its display
	// title). Accept both shapes so this client can deploy ahead of the API.
	if (data && !Array.isArray(data) && Array.isArray(data.items)) {
		const total = Number(data.totalCount);
		return {
			items: data.items,
			totalCount: Number.isFinite(total) && total >= 0 ? total : data.items.length,
			lookupTitles: (data.lookupTitles && typeof data.lookupTitles === 'object') ? data.lookupTitles : {},
		};
	}
	const items = Array.isArray(data) ? data : [];
	const total = Number(headers?.['x-total-count']);
	return {
		items,
		totalCount: Number.isFinite(total) && total >= 0 ? total : items.length,
		lookupTitles: {},
	};
}

/**
 * Submit an intake form — the platform's one public WRITE
 * (`POST /api/share/{code}/submit`, no auth). Answers `{ ok, confirmation,
 * allowAnother }` and NEVER the created item: the visitor has no read of it,
 * and it lands quarantined for a person in the collection to review.
 *
 * The payload is closed: `submissionToken`, `turnstileToken`, `elements`,
 * nothing else (an `owner` or `tags` is a 400, not ignored). No `notes`:
 * public forms no longer offer a notes box (KPR-792). Every
 * dead-link state is the same flat 404; a 400 is about THIS payload and
 * carries a `code` (see utils/intakeLinks describeSubmitError). A replayed
 * token answers 200 again and writes no second row.
 */
export async function submitSharedForm(code, {
	submissionToken, turnstileToken, elements,
} = {}) {
	const payload = { submissionToken, elements: elements || {} };
	if (turnstileToken) payload.turnstileToken = turnstileToken;
	const { data } = await api.post(
		`${API_PUBLIC_SHARE_URL}/${encodeURIComponent(code)}/submit`,
		payload
	);
	return data;
}

/** The error a public byte call throws: axios-shaped, so callers read it as any other. */
function publicCallError(status, data, retryAfter = null, fallback = 'That did not go through.') {
	const error = new Error((data && data.message) || fallback);
	error.response = { status, data, headers: retryAfter ? { 'retry-after': retryAfter } : {} };
	return error;
}

/**
 * Upload one file to a public form (KPR-388): `POST
 * /api/share/{code}/uploads?element=<name>`, no account. The BODY is the
 * file's bytes (never multipart: the API judges the link, the token and the
 * caps before it reads a byte of it), the form's submission token rides in
 * `X-Submission-Token`. Answers `{ id, element, kind, name, size, image }` —
 * the id goes in the element's value at Send.
 *
 * ⚑ XMLHttpRequest, never the shared client: like a report, an upload is a
 * VISITOR's, and the bearer of whoever is signed in on this machine must not
 * travel with it (`withCredentials` off, no Authorization); and XHR is what
 * reports upload progress (`onProgress(percent)`). `signal` aborts it. A
 * refusal throws an axios-shaped error (`response: { status, data, headers }`
 * — `retry-after` read from the exposed header).
 */
export function uploadSharedFile(code, { file, element, submissionToken } = {}, { onProgress, signal } = {}) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		const url = `${API_PUBLIC_SHARE_URL}/${encodeURIComponent(code)}/uploads?element=${encodeURIComponent(element || '')}`;
		xhr.open('POST', url);
		xhr.withCredentials = false;
		xhr.setRequestHeader('X-Submission-Token', submissionToken || '');
		xhr.setRequestHeader('Content-Type', (file && file.type) || 'application/octet-stream');
		if (xhr.upload && typeof onProgress === 'function') {
			xhr.upload.onprogress = (event) => {
				if (event && event.lengthComputable && event.total > 0) onProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
			};
		}
		xhr.onload = () => {
			let data = null;
			try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { data = null; }
			if (xhr.status >= 200 && xhr.status < 300) { resolve(data); return; }
			reject(publicCallError(xhr.status, data, xhr.getResponseHeader('Retry-After'), `The upload was refused (${xhr.status}).`));
		};
		xhr.onerror = () => reject(publicCallError(0, null, null, 'The upload did not reach keepr. Check the connection and try again.'));
		xhr.onabort = () => {
			const error = publicCallError(0, null, null, 'The upload was stopped.');
			error.aborted = true;
			reject(error);
		};
		if (signal) {
			if (signal.aborted) { xhr.abort(); return; }
			signal.addEventListener('abort', () => xhr.abort(), { once: true });
		}
		xhr.send(file);
	});
}

/**
 * Take one of this form's uploads back before sending (KPR-388): `DELETE
 * /api/share/{code}/uploads/{id}` with the token the upload was sent under.
 * The answer is the same whether or not the id was the token's, so there is
 * nothing to read; a failure is harmless (the server deletes an unsent
 * upload on its own within the hour). A plain fetch, no credentials, for the
 * reason above.
 */
export async function removeSharedUpload(code, uploadId, submissionToken) {
	const res = await fetch(`${API_PUBLIC_SHARE_URL}/${encodeURIComponent(code)}/uploads/${encodeURIComponent(uploadId)}`, {
		method: 'DELETE',
		headers: { 'X-Submission-Token': submissionToken || '' },
		credentials: 'omit',
	});
	if (!res.ok) {
		const data = await res.json().catch(() => null);
		throw publicCallError(res.status, data, null, 'The upload could not be removed.');
	}
	return true;
}

/**
 * "Report this page" (G16): `POST /api/share/{code}/report`, no account — a
 * reason from the fixed list (utils/shareReports.js) and, optionally, a
 * few words (`text`, at most 500 characters; the payload is closed). It lands
 * in the admins' review list and notifies nobody else. A dead code is the same flat 404 as everywhere.
 *
 * ⚑ A plain `fetch`, never the shared client: a report is a VISITOR's, so the
 * bearer of whoever is signed in on this machine must not travel with it
 * (the 10c security review) — as the public byte URLs carry none. A refusal
 * throws an axios-shaped error (`response: { status, data }`) so callers read
 * it as they read any other.
 */
export async function reportSharedPage(code, { reason, text } = {}) {
	const payload = { reason };
	const words = typeof text === 'string' ? text.trim() : '';
	if (words) payload.text = words;
	const res = await fetch(`${API_PUBLIC_SHARE_URL}/${encodeURIComponent(code)}/report`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload),
		credentials: 'omit',
	});
	const data = await res.json().catch(() => null);
	if (!res.ok) {
		const error = new Error((data && data.message) || `The report was refused (${res.status}).`);
		error.response = { status: res.status, data };
		throw error;
	}
	return data;
}

/**
 * A share-link refusal in words. A public link's filter may not walk a
 * lookup into another collection (400 `cross_collection_hop_not_allowed`,
 * keepr-api § KQL "Stored queries do not cross"): the page has no reader to
 * resolve it for, so it would silently show fewer records than meant. That
 * one gets the plain sentence; everything else keeps the API's message.
 */
export function describeShareLinkError(error, fallback = 'Could not create the share link.') {
	if (apiErrorCode(error) === 'cross_collection_hop_not_allowed') return SHARE_LINK_HOP_MESSAGE;
	return error?.response?.data?.message || fallback;
}
